import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { HiOutlineCloudArrowUp, HiOutlineFolder } from "react-icons/hi2";
import {
  useTeamFiles,
  usePresignTeamUpload,
  useConfirmTeamUpload,
  useTeamFileUrl,
  useDeleteTeamFile,
  useCreateTeamShareLink,
  useCreateTeamFolder,
  useUpdateTeamFolder,
  useDeleteTeamFolder,
  useTeamFolderTree,
  useMoveTeamFile,
} from "../../lib/react-query/teamFiles.js";
import { useTeamDetail } from "../../lib/react-query/teams";
import { useDebounce, extractBackendErrorMessage } from "../../utils/utils";
import { showToast, ConfirmModal, Button, Divider } from "../shared";
import { UsageMeter } from "../Files/UsageMeter";
import { FileList } from "../Files/FileList";
import { FileUploader } from "../Files/FileUploader";
import { FileViewer } from "../Files/FileViewer";
import { FileShareModal } from "../Files/FileShareModal";
import { FileDetailsPanel } from "../Files/FileDetailsPanel";
import { FolderBreadcrumbs } from "../Files/FolderBreadcrumbs";
import {
  FolderCreateModal,
  FolderRenameModal,
  FolderMoveModal,
  FolderDeleteModal,
} from "../Files/FolderModals";
import { TeamsIcon } from "../../assets/icons";
import { useFileManagerStore } from "../../stores/fileManagerStore";
import { useShallow } from "zustand/react/shallow";

const TeamStorageFileManager = ({ teamId }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: team } = useTeamDetail(teamId, !!teamId);
  const fileInputRef = useRef(null);
  const activeUploadsRef = useRef(new Set());
  const xhrMapRef = useRef(new Map());
  const previewRequestRef = useRef(0);

  const currentUser = useSelector((state) => state?.user?.user);
  const canManage = team?.owner === currentUser?.id || ["OWNER", "ADMIN"].includes(
    team?.members?.find((m) => m.user?.id === currentUser?.id)?.role
  );

  const {
    searchQuery,
    sortBy,
    sortOrder,
    filterType,
    page,
    uploadQueue,
    viewModalOpen,
    viewerFile,
    shareModalOpen,
    shareFileData,
    deleteModalOpen,
    deletingFile,
    currentFolderId,
    selectedFileId,
    createFolderOpen,
    renameItem,
    moveItem,
    deleteFolder,
    setSearchQuery,
    setFilterType,
    setSort,
    setPage,
    openViewer,
    closeViewer,
    openShareModal,
    closeShareModal,
    openDeleteModal,
    closeDeleteModal,
    setCurrentFolderId,
    openCreateFolder,
    closeCreateFolder,
    openRename,
    closeRename,
    openMove,
    closeMove,
    openDeleteFolder,
    closeDeleteFolder,
    enqueueFiles,
    updateUpload,
    removeUpload,
    clearCompletedUploads,
    setSelectedFileId,
    reset,
  } = useFileManagerStore(
    useShallow((state) => ({
      searchQuery: state.searchQuery,
      sortBy: state.sortBy,
      sortOrder: state.sortOrder,
      filterType: state.filterType,
      page: state.page,
      uploadQueue: state.uploadQueue,
      selectedFileId: state.selectedFileId,
      viewModalOpen: state.viewModalOpen,
      viewerFile: state.viewerFile,
      shareModalOpen: state.shareModalOpen,
      shareFileData: state.shareFileData,
      deleteModalOpen: state.deleteModalOpen,
      deletingFile: state.deletingFile,
      currentFolderId: state.currentFolderId,
      createFolderOpen: state.createFolderOpen,
      renameItem: state.renameItem,
      moveItem: state.moveItem,
      deleteFolder: state.deleteFolder,
      setSearchQuery: state.setSearchQuery,
      setFilterType: state.setFilterType,
      setSort: state.setSort,
      setPage: state.setPage,
      setSelectedFileId: state.setSelectedFileId,
      openViewer: state.openViewer,
      closeViewer: state.closeViewer,
      openShareModal: state.openShareModal,
      closeShareModal: state.closeShareModal,
      openDeleteModal: state.openDeleteModal,
      closeDeleteModal: state.closeDeleteModal,
      setCurrentFolderId: state.setCurrentFolderId,
      openCreateFolder: state.openCreateFolder,
      closeCreateFolder: state.closeCreateFolder,
      openRename: state.openRename,
      closeRename: state.closeRename,
      openMove: state.openMove,
      closeMove: state.closeMove,
      openDeleteFolder: state.openDeleteFolder,
      closeDeleteFolder: state.closeDeleteFolder,
      enqueueFiles: state.enqueueFiles,
      updateUpload: state.updateUpload,
      removeUpload: state.removeUpload,
      clearCompletedUploads: state.clearCompletedUploads,
      reset: state.reset,
    }))
  );

  useEffect(() => {
    return () => reset();
  }, [teamId, reset]);

  useEffect(() => {
    const urlFolder = searchParams.get("folder");
    const parsed = urlFolder ? Number(urlFolder) : null;
    const next = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    if (next !== currentFolderId) {
      setCurrentFolderId(next);
    }
  }, [searchParams, currentFolderId, setCurrentFolderId]);

  const navigateToFolder = useCallback(
    (id) => {
      const next = new URLSearchParams(searchParams);
      if (id != null) {
        next.set("folder", String(id));
      } else {
        next.delete("folder");
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const debouncedSearch = useDebounce(searchQuery, 300);
  const ordering = sortOrder === "asc" ? sortBy : `-${sortBy}`;

  const { data: fileData, isLoading: filesLoading, error: filesError } = useTeamFiles(
    teamId,
    {
      search: debouncedSearch,
      ordering,
      category: filterType,
      page,
      page_size: 50,
      folder_id: currentFolderId,
    },
    !!teamId
  );

  const { data: folderTreeData } = useTeamFolderTree(teamId, !!teamId && moveItem != null);

  const presignMutation = usePresignTeamUpload();
  const confirmMutation = useConfirmTeamUpload();
  const fileUrlMutation = useTeamFileUrl();
  const deleteMutation = useDeleteTeamFile();
  const shareMutation = useCreateTeamShareLink();
  const createFolderMutation = useCreateTeamFolder();
  const updateFolderMutation = useUpdateTeamFolder();
  const deleteFolderMutation = useDeleteTeamFolder();
  const moveFileMutation = useMoveTeamFile();

  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFileId, setPreviewFileId] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [shareResult, setShareResult] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [modalError, setModalError] = useState(null);

  const folders = useMemo(() => fileData?.folders || [], [fileData]);
  const files = useMemo(() => fileData?.items || [], [fileData]);
  const permissions = fileData?.permissions || {};
  const usage = fileData?.usage || { used_bytes: 0, quota_bytes: 0 };
  const breadcrumbs = fileData?.breadcrumbs || [];
  const currentFolder = fileData?.current_folder || null;
  const usagePercent = usage.quota_bytes > 0 ? (usage.used_bytes / usage.quota_bytes) * 100 : 0;
  const selectedFile = viewerFile || files.find((file) => file.id === selectedFileId) || files[0] || null;
  const isUploading = uploadQueue.some((item) => item.status === "pending" || item.status === "uploading");

  useEffect(() => {
    if (!selectedFileId && files.length > 0) {
      setSelectedFileId(files[0].id);
    }
    if (selectedFileId && !files.some((file) => file.id === selectedFileId)) {
      setSelectedFileId(files[0]?.id || null);
    }
  }, [files, selectedFileId, setSelectedFileId]);

  useEffect(() => {
    if (!modalError) return;
    const id = setTimeout(() => setModalError(null), 4000);
    return () => clearTimeout(id);
  }, [modalError]);

  const uploadFileToS3 = useCallback(
    (uploadItem, presignedData, index) => {
      const { id, file } = uploadItem;
      const presignedItem = presignedData.files[index];
      if (!presignedItem) {
        updateUpload(id, { status: "error", error: "Presigned URL not found for file" });
        return;
      }

      activeUploadsRef.current.add(id);
      updateUpload(id, { status: "uploading", startedAt: Date.now() });

      const formData = new FormData();
      Object.entries(presignedItem.fields).forEach(([key, value]) => formData.append(key, value));
      formData.append("file", file);

      const xhr = new XMLHttpRequest();
      xhrMapRef.current.set(id, xhr);
      let lastLoaded = 0;
      let lastTime = Date.now();

      xhr.upload.addEventListener("progress", (e) => {
        if (!e.lengthComputable) return;
        const now = Date.now();
        const elapsed = (now - lastTime) / 1000;
        const deltaBytes = e.loaded - lastLoaded;
        const speed = elapsed > 0 ? deltaBytes / elapsed : 0;
        const remaining = e.total - e.loaded;
        const eta = speed > 0 ? remaining / speed : null;
        lastLoaded = e.loaded;
        lastTime = now;
        updateUpload(id, { progress: Math.round((e.loaded / e.total) * 100), speed, eta });
      });

      xhr.addEventListener("load", async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            await confirmMutation.mutateAsync({
              teamId,
              payload: {
                s3_key: presignedItem.s3_key,
                original_name: file.name,
                content_type: file.type,
                size_bytes: file.size,
                folder_id: presignedItem.folder_id,
              },
            });
            updateUpload(id, { status: "success", progress: 100, speed: 0, eta: 0 });
          } catch (err) {
            updateUpload(id, { status: "error", error: extractBackendErrorMessage(err, "Upload confirmation failed") });
          }
        } else {
          updateUpload(id, { status: "error", error: `Upload failed (${xhr.status})` });
        }
        activeUploadsRef.current.delete(id);
        xhrMapRef.current.delete(id);
      });

      xhr.addEventListener("error", () => {
        updateUpload(id, { status: "error", error: "Connection lost during upload. Check your network and try again." });
        activeUploadsRef.current.delete(id);
        xhrMapRef.current.delete(id);
      });

      xhr.addEventListener("abort", () => {
        updateUpload(id, { status: "error", error: "Upload cancelled" });
        activeUploadsRef.current.delete(id);
        xhrMapRef.current.delete(id);
      });

      xhr.open("POST", presignedItem.upload_url);
      xhr.send(formData);
    },
    [confirmMutation, teamId, updateUpload]
  );

  useEffect(() => {
    const pendingItems = uploadQueue.filter((item) => item.status === "pending" && !activeUploadsRef.current.has(item.id));
    if (pendingItems.length === 0) return;

    pendingItems.forEach((item) => activeUploadsRef.current.add(item.id));

    const startUpload = async () => {
      const filesPayload = pendingItems.map((item) => ({
        original_name: item.file.name,
        content_type: item.file.type,
        size_bytes: item.file.size,
      }));

      try {
        const presignedData = await presignMutation.mutateAsync({
          teamId,
          files: filesPayload,
          folderId: currentFolderId,
        });
        pendingItems.forEach((item, idx) => uploadFileToS3(item, presignedData, idx));
      } catch (err) {
        pendingItems.forEach((item) =>
          updateUpload(item.id, {
            status: "error",
            error: extractBackendErrorMessage(err, "Failed to get upload URL"),
          })
        );
        pendingItems.forEach((item) => activeUploadsRef.current.delete(item.id));
      }
    };

    startUpload();
  }, [uploadQueue, presignMutation, teamId, updateUpload, uploadFileToS3, currentFolderId]);

  const handleRetry = useCallback(
    (item) => updateUpload(item.id, { status: "pending", progress: 0, error: null, speed: 0, eta: null }),
    [updateUpload]
  );

  const handleRemoveUpload = useCallback((id) => removeUpload(id), [removeUpload]);

  const loadPreviewUrl = useCallback(
    async (file) => {
      const requestId = ++previewRequestRef.current;
      setPreviewFileId(file.id);
      setPreviewUrl(null);
      setPreviewError(null);
      try {
        const url = await fileUrlMutation.mutateAsync({ teamId, fileId: file.id });
        if (requestId === previewRequestRef.current) {
          setPreviewUrl(url);
        }
        return url;
      } catch (err) {
        if (requestId === previewRequestRef.current) {
          setPreviewError(extractBackendErrorMessage(err, "Failed to load preview"));
          setPreviewUrl(null);
        }
        throw err;
      }
    },
    [fileUrlMutation, teamId]
  );

  const handlePreview = useCallback(
    async (file) => {
      setSelectedFileId(file.id);
      openViewer(file);
      try {
        await loadPreviewUrl(file);
      } catch {
        showToast("Failed to load preview", "error");
      }
    },
    [loadPreviewUrl, openViewer, setSelectedFileId]
  );

  const handleLoadPreview = useCallback(
    async (file) => {
      try {
        await loadPreviewUrl(file);
      } catch {
        showToast("Failed to load preview", "error");
        throw new Error("preview failed");
      }
    },
    [loadPreviewUrl]
  );

  const handleDownload = useCallback(
    async (file) => {
      try {
        const url = await fileUrlMutation.mutateAsync({ teamId, fileId: file.id });
        const a = document.createElement("a");
        a.href = url;
        a.download = file.original_name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch {
        showToast("Failed to download file", "error");
      }
    },
    [teamId, fileUrlMutation]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingFile) return;
    try {
      await deleteMutation.mutateAsync({ teamId, fileId: deletingFile.id });
      showToast("File deleted", "success");
      closeDeleteModal();
    } catch {
      showToast("Failed to delete file", "error");
    }
  }, [deletingFile, teamId, deleteMutation, closeDeleteModal]);

  const handleShareGenerate = useCallback(
    async ({ fileId, expiryHours, expiresAt }) => {
      try {
        const result = await shareMutation.mutateAsync({ teamId, fileId, expiryHours, expiresAt });
        setShareResult(result);
      } catch (err) {
        showToast(extractBackendErrorMessage(err, "Failed to create share link"), "error");
      }
    },
    [teamId, shareMutation]
  );

  const handleCloseViewer = useCallback(() => {
    previewRequestRef.current += 1;
    closeViewer();
    setPreviewUrl(null);
    setPreviewError(null);
  }, [closeViewer]);

  const handleOpenDetails = useCallback((file) => {
    setSelectedFileId(file.id);
    setDetailsModalOpen(true);
  }, [setSelectedFileId]);

  const handleCloseDetails = useCallback(() => {
    setDetailsModalOpen(false);
  }, []);

  const handleCloseShare = useCallback(() => {
    closeShareModal();
    setShareResult(null);
  }, [closeShareModal]);

  const handleOpenFolder = useCallback(
    (folder) => {
      if (!folder) return;
      navigateToFolder(folder.id);
    },
    [navigateToFolder]
  );

  const handleBreadcrumbNavigate = useCallback(
    (folderId) => {
      navigateToFolder(folderId);
    },
    [navigateToFolder]
  );

  const handleCreateFolder = useCallback(
    async ({ name, parentId }) => {
      setModalError(null);
      try {
        await createFolderMutation.mutateAsync({ teamId, name, parentId });
        showToast("Folder created", "success");
        closeCreateFolder();
      } catch (err) {
        setModalError(extractBackendErrorMessage(err, "Failed to create folder"));
      }
    },
    [createFolderMutation, teamId, closeCreateFolder]
  );

  const handleRenameSubmit = useCallback(
    async ({ name }) => {
      if (!renameItem) return;
      setModalError(null);
      try {
        if (renameItem.kind === "folder") {
          await updateFolderMutation.mutateAsync({ teamId, folderId: renameItem.id, name });
        } else {
          showToast("File rename is not supported", "info");
          closeRename();
          return;
        }
        showToast("Folder renamed", "success");
        closeRename();
      } catch (err) {
        setModalError(extractBackendErrorMessage(err, "Failed to rename"));
      }
    },
    [renameItem, updateFolderMutation, teamId, closeRename]
  );

  const handleMoveSubmit = useCallback(
    async ({ folderId }) => {
      if (!moveItem) return;
      const sourceExists =
        moveItem.kind === "folder"
          ? folders.some((folder) => folder.id === moveItem.id)
          : files.some((file) => file.id === moveItem.id);
      if (!sourceExists) {
        setModalError("Move source is stale. Reopen the menu and try again.");
        return;
      }
      setModalError(null);
      try {
        if (moveItem.kind === "folder") {
          await updateFolderMutation.mutateAsync({
            teamId,
            folderId: moveItem.id,
            parentId: folderId,
          });
        } else {
          await moveFileMutation.mutateAsync({
            teamId,
            fileId: moveItem.id,
            folderId,
          });
        }
        showToast("Moved", "success");
        closeMove();
      } catch (err) {
        setModalError(extractBackendErrorMessage(err, "Failed to move"));
      }
    },
    [moveItem, folders, files, updateFolderMutation, moveFileMutation, teamId, closeMove]
  );

  const handleDeleteFolderConfirm = useCallback(async () => {
    if (!deleteFolder) return;
    setModalError(null);
    try {
      await deleteFolderMutation.mutateAsync({ teamId, folderId: deleteFolder.id });
      showToast("Folder deleted", "success");
      if (currentFolderId === deleteFolder.id) {
        const parentId = deleteFolder.parent ?? null;
        navigateToFolder(parentId);
      }
      closeDeleteFolder();
    } catch (err) {
      setModalError(extractBackendErrorMessage(err, "Failed to delete folder"));
    }
  }, [deleteFolder, deleteFolderMutation, teamId, currentFolderId, navigateToFolder, closeDeleteFolder]);

  const handleRenameFolder = useCallback(
    (folder) => openRename({ ...folder, kind: "folder" }),
    [openRename]
  );

  const handleMoveFolder = useCallback(
    (folder) =>
      openMove({
        ...folder,
        kind: "folder",
        teamId,
        currentFolderId: folder.parent ?? currentFolderId,
      }),
    [openMove, currentFolderId, teamId]
  );

  const handleMoveFile = useCallback(
    (file) => openMove({ ...file, kind: "file", teamId, currentFolderId: file.folder_id ?? currentFolderId }),
    [openMove, currentFolderId, teamId]
  );

  const handleRenameFile = useCallback(
    (file) => openRename({ ...file, kind: "file" }),
    [openRename]
  );

  if (!Number.isFinite(teamId)) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-sm text-grey">Invalid team ID</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-surface-border overflow-hidden">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0 px-4 py-3">
          <div className="font-sans flex items-center gap-3 w-full sm:w-auto">
            <TeamsIcon width={28} height={28} className="text-primary flex-shrink-0" />
            <div className="flex items-center gap-1">
              <span className="text-sm font-semibold max-w-[150px] truncate inline-block align-bottom">
                {team?.name || "Team"}
              </span>
              <span className="text-grey">/</span>
              <span className="text-sm">Files</span>
            </div>
          </div>
          <div className="font-sans flex items-center gap-3 w-full sm:w-auto justify-end">
            {permissions.can_manage_folders !== false && canManage && (
              <Button
                variant="outlined"
                color="secondary"
                size="md"
                icon={HiOutlineFolder}
                onClick={openCreateFolder}
                className="gap-2 w-full sm:w-auto justify-center"
              >
                New Folder
              </Button>
            )}
            {permissions.can_upload !== false && canManage && (
              <Button
                variant="filled"
                color="secondary"
                size="md"
                icon={HiOutlineCloudArrowUp}
                onClick={() => fileInputRef.current?.click()}
                className="gap-2 w-full sm:w-auto justify-center"
              >
                Upload Files
              </Button>
            )}
          </div>
        </div>
        <div className="px-2">
          <Divider />
        </div>
        {!currentFolder?.is_root && (
          <div className="px-4 pt-3">
            <FolderBreadcrumbs
              breadcrumbs={breadcrumbs}
              onNavigate={handleBreadcrumbNavigate}
              currentFolderId={currentFolderId}
            />
          </div>
        )}
      </div>

      {filesError && (
        <div className="rounded-xl border border-danger/30 bg-danger/5 p-4 text-sm text-danger">
          Failed to load files. {(filesError?.response?.data?.message) || "Please try again."}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <UsageMeter usedBytes={usage.used_bytes} quotaBytes={usage.quota_bytes} isLoading={filesLoading} />
        <FileUploader
          ref={fileInputRef}
          queue={uploadQueue}
          isUploading={isUploading}
          onEnqueue={enqueueFiles}
          onRetry={handleRetry}
          onRemove={handleRemoveUpload}
          onClearCompleted={clearCompletedUploads}
          canUpload={permissions.can_upload !== false && canManage}
          usagePercent={usagePercent}
        />
      </div>

      <FileList
        folders={folders}
        files={files}
        count={fileData?.count || 0}
        page={fileData?.page || page}
        totalPages={fileData?.total_pages || 1}
        isLoading={filesLoading}
        searchQuery={searchQuery}
        filterType={filterType}
        sortBy={sortBy}
        sortOrder={sortOrder}
        permissions={permissions}
        selectedFileId={selectedFile?.id}
        currentFolderName={currentFolder?.name}
        onSearchChange={setSearchQuery}
        onFilterChange={setFilterType}
        onSortChange={(field) => setSort(field)}
        onSortSet={setSort}
        onPageChange={setPage}
        onSelectFile={handleOpenDetails}
        onPreview={handlePreview}
        onDownload={handleDownload}
        onShare={openShareModal}
        onDelete={openDeleteModal}
        onOpenFolder={handleOpenFolder}
        onRenameItem={handleRenameFolder}
        onMoveFolder={handleMoveFolder}
        onMoveFile={handleMoveFile}
        onDeleteFolder={permissions.can_delete && canManage ? openDeleteFolder : undefined}
      />

      <FileDetailsPanel
        file={selectedFile}
        canShare={permissions.can_share && canManage}
        canDelete={permissions.can_delete && canManage}
        isOpen={detailsModalOpen}
        onClose={handleCloseDetails}
        onDownload={handleDownload}
        onShare={openShareModal}
        onDelete={openDeleteModal}
        onMove={permissions.can_manage_folders && canManage ? handleMoveFile : undefined}
        onRename={handleRenameFile}
        previewUrl={selectedFile?.id === previewFileId ? previewUrl : null}
        previewError={selectedFile?.id === previewFileId ? previewError : null}
        onLoadPreview={handleLoadPreview}
      />

      <FileViewer
        file={viewerFile}
        previewUrl={previewUrl}
        previewError={previewError}
        isOpen={viewModalOpen}
        onClose={handleCloseViewer}
        onDownload={() => viewerFile && handleDownload(viewerFile)}
        onShare={() => {
          if (viewerFile) {
            closeViewer();
            setPreviewUrl(null);
            previewRequestRef.current += 1;
            openShareModal(viewerFile);
          }
        }}
        canShare={permissions.can_share && canManage}
      />

      <FileShareModal
        isOpen={shareModalOpen}
        onClose={handleCloseShare}
        file={shareFileData}
        onGenerate={handleShareGenerate}
        isLoading={shareMutation.isPending}
        shareResult={shareResult}
      />

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        title="Delete File"
        message={`Are you sure you want to delete "${deletingFile?.original_name}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmColor="danger"
        isLoading={deleteMutation.isPending}
      />

      <FolderCreateModal
        isOpen={createFolderOpen}
        onClose={closeCreateFolder}
        onSubmit={handleCreateFolder}
        currentFolderId={currentFolderId}
        isLoading={createFolderMutation.isPending}
        errorMessage={modalError}
      />

      <FolderRenameModal
        isOpen={renameItem != null}
        onClose={closeRename}
        onSubmit={handleRenameSubmit}
        item={renameItem}
        isLoading={updateFolderMutation.isPending}
        errorMessage={modalError}
      />

      <FolderMoveModal
        isOpen={moveItem != null}
        onClose={closeMove}
        onSubmit={handleMoveSubmit}
        item={moveItem}
        tree={folderTreeData?.tree}
        isLoading={updateFolderMutation.isPending || moveFileMutation.isPending}
        errorMessage={modalError}
      />

      <FolderDeleteModal
        isOpen={deleteFolder != null}
        onClose={closeDeleteFolder}
        onConfirm={handleDeleteFolderConfirm}
        folder={deleteFolder}
        isLoading={deleteFolderMutation.isPending}
        errorMessage={modalError}
      />
    </div>
  );
};

export default TeamStorageFileManager;
